import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Shield, 
  Search, 
  RefreshCw, 
  Loader2,
  User,
  Settings,
  FileText,
  MessageSquare,
  CreditCard,
  Key,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const actionIcons = {
  login: Key,
  logout: Key,
  register: User,
  create: FileText,
  update: Settings,
  delete: AlertTriangle,
  chatbot: MessageSquare,
  payment: CreditCard,
  upgrade: CreditCard,
  downgrade: CreditCard,
  cancel: AlertTriangle,
  password_reset: Key,
  document_upload: FileText,
  document_delete: AlertTriangle,
  user: User,
  default: Activity
};

const actionColors = {
  login: 'bg-green-100 text-green-700 border-green-200',
  logout: 'bg-slate-100 text-slate-600 border-slate-200',
  register: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  create: 'bg-blue-100 text-blue-700 border-blue-200',
  update: 'bg-amber-100 text-amber-700 border-amber-200',
  delete: 'bg-red-100 text-red-700 border-red-200',
  upgrade: 'bg-purple-100 text-purple-700 border-purple-200',
  downgrade: 'bg-orange-100 text-orange-700 border-orange-200',
  cancel: 'bg-red-100 text-red-700 border-red-200',
  password_reset: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  document_upload: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  document_delete: 'bg-pink-100 text-pink-700 border-pink-200',
  error: 'bg-red-100 text-red-700 border-red-200',
  default: 'bg-slate-100 text-slate-600 border-slate-200'
};

/**
 * Render `log.details` as a compact, human-readable summary instead of raw
 * truncated JSON (fixes iter124 bug #6). Recognises common shapes:
 *   • { name: "...", email: "..." }               → "Alice · alice@x.com"
 *   • { chatbot_id: "cb_abc", chatbot_name: "…"} → "AI Employee: NymphSolutions"
 *   • { role: "moderator" }                       → "Role: moderator"
 * Falls back to comma-separated key: value pairs, escaping unreadable
 * blobs. Never returns the raw JSON string.
 */
const humanizeDetails = (details) => {
  if (!details) return '—';
  if (typeof details === 'string') {
    // Some legacy rows already store a plain sentence.
    return details.length > 160 ? details.slice(0, 160) + '…' : details;
  }
  if (typeof details !== 'object') return String(details);
  const parts = [];
  const push = (label, val) => {
    if (val === null || val === undefined || val === '') return;
    const str = typeof val === 'string' ? val : JSON.stringify(val);
    parts.push(label ? `${label}: ${str}` : str);
  };
  // Priority fields in a nice order
  if (details.name) push('', details.name);
  if (details.email) push('email', details.email);
  if (details.chatbot_name) push('AI Employee', details.chatbot_name);
  if (details.chatbot_id && !details.chatbot_name) push('AI Employee', details.chatbot_id);
  if (details.type) push('type', details.type);
  if (details.role) push('role', details.role);
  if (details.plan) push('plan', details.plan);
  if (details.amount != null) push('amount', details.amount);
  if (details.status) push('status', details.status);
  if (details.reason) push('reason', details.reason);
  if (details.count != null) push('count', details.count);
  // Any other keys that weren't handled above
  const handled = new Set(['name', 'email', 'chatbot_name', 'chatbot_id', 'type', 'role', 'plan', 'amount', 'status', 'reason', 'count']);
  for (const [k, v] of Object.entries(details)) {
    if (handled.has(k)) continue;
    if (typeof v === 'object' && v !== null) continue; // skip nested objects
    push(k, v);
    if (parts.length >= 5) break;
  }
  if (parts.length === 0) return '—';
  const line = parts.join(' · ');
  return line.length > 160 ? line.slice(0, 160) + '…' : line;
};

/**
 * Wrap every case-insensitive substring match of `q` in a <mark>. Used to
 * surface which fragment of a partial User-ID search matched (iter124 bug #12).
 */
const highlight = (text, q) => {
  if (!q || !text) return text;
  try {
    const escaped = String(q).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = String(text).split(new RegExp(`(${escaped})`, 'ig'));
    return parts.map((chunk, i) => (
      chunk.toLowerCase() === String(q).toLowerCase()
        ? <mark key={`m-${i}`} className="bg-yellow-200 text-slate-900 rounded px-0.5">{chunk}</mark>
        : <React.Fragment key={`t-${i}`}>{chunk}</React.Fragment>
    ));
  } catch {
    return text;
  }
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [filters, setFilters] = useState({
    action_type: '',
    entity_type: '',
    user_id: '',
    search: ''
  });

  useEffect(() => {
    loadAuditLogs();
    loadSummary();
    // Include user_id + search so typing in the search field actually
    // refetches. Previously the search field was purely cosmetic — the
    // page never re-fetched when the User ID changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.action_type, filters.entity_type, filters.user_id]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 20);
      if (filters.action_type) params.append('action', filters.action_type);
      if (filters.entity_type) params.append('resource_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id.trim());

      const response = await api.get(`/audit/logs?${params}`);
      setLogs(response.data.logs || []);
      // Backend returns `total` (not `total_pages`); compute pages here.
      const total = Number(response.data.total || 0);
      setTotalPages(Math.max(1, Math.ceil(total / 20)));
      setTotalEvents(total);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await api.get('/audit/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const getActionIcon = (action) => {
    const Icon = actionIcons[action?.split('_')[0]] || actionIcons.default;
    return Icon;
  };

  const getActionColor = (action) => {
    const key = action?.split('_')[0] || 'default';
    return actionColors[key] || actionColors.default;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-600" />
              Audit Logs
            </h1>
            <p className="text-slate-600 mt-1">Monitor system activity and user actions</p>
          </div>
          <Button onClick={() => { loadAuditLogs(); loadSummary(); }} variant="outline" className="border-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white border-slate-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-slate-900">{totalEvents ?? (summary.total_logs || 0)}</p>
                <p className="text-sm text-slate-500">Total Events</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-green-700">{summary.logins_today || 0}</p>
                <p className="text-sm text-green-600">Logins Today</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-blue-700">{summary.actions_today || 0}</p>
                <p className="text-sm text-blue-600">Actions Today</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <p className="text-2xl font-bold text-red-700">{summary.errors_today || 0}</p>
                <p className="text-sm text-red-600">Errors Today</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by user ID..."
                    value={filters.user_id}
                    onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && loadAuditLogs()}
                    className="pl-10 bg-white border-slate-300"
                  />
                </div>
              </div>
              <Select 
                value={filters.action_type || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, action_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-44 bg-white border-slate-300">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                  <SelectItem value="register">Register</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="upgrade">Upgrade</SelectItem>
                  <SelectItem value="cancel">Cancel</SelectItem>
                  <SelectItem value="password_reset">Password Change</SelectItem>
                  <SelectItem value="document_upload">Document Upload</SelectItem>
                  <SelectItem value="document_delete">Document Delete</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={filters.entity_type || 'all'} 
                onValueChange={(v) => setFilters({ ...filters, entity_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="w-40 bg-white border-slate-300">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="chatbot">AI Employee</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="session">Session</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadAuditLogs} className="bg-blue-600 hover:bg-blue-700">
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Activity Log</CardTitle>
            <CardDescription className="text-slate-600">
              Detailed record of all system activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600">No audit logs found</p>
                <p className="text-sm text-slate-500 mt-1">Activity will appear here as users interact with the system</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Timestamp</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Action</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Resource</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Details</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, idx) => {
                        const ActionIcon = getActionIcon(log.action);
                        return (
                          <tr key={log.log_id || idx} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="p-3 text-sm text-slate-600">
                              {formatTimestamp(log.timestamp)}
                            </td>
                            <td className="p-3">
                              <Badge className={getActionColor(log.action)}>
                                <ActionIcon className="w-3 h-3 mr-1" />
                                {log.action || 'unknown'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <span className="text-sm text-slate-700 capitalize">
                                {log.resource_type || '-'}
                              </span>
                              {log.resource_id && (
                                <p className="text-xs text-slate-400 font-mono truncate max-w-[120px]" title={log.resource_id}>
                                  {log.resource_id}
                                </p>
                              )}
                            </td>
                            <td className="p-3">
                              <span className="text-sm text-slate-700">
                                {highlight(log.user_email || log.user_id || 'System', filters.user_id)}
                              </span>
                            </td>
                            <td className="p-3">
                              <span
                                className="text-sm text-slate-600 truncate max-w-[280px] block"
                                title={log.details ? JSON.stringify(log.details) : ''}
                              >
                                {humanizeDetails(log.details)}
                              </span>
                            </td>
                            <td className="p-3 text-sm text-slate-500 font-mono">
                              {log.ip_address || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="border-slate-300"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="border-slate-300"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditLogsPage;
